// MasteryHeroSettings — popover dropdown next to the rotator that lets
// the visitor (a) tune how fast the trio of skills rotates, and (b)
// reorder the entire 100-step path with drag-and-drop so it becomes
// THEIR voyage instead of our prescribed one.
//
// Why a popover (not a modal): the home is a launchpad. We don't want
// to yank visitors out of the hero into a settings page. The popover
// keeps the rotator visible behind it so they can see their changes
// take effect immediately ("ah, that's faster") without losing context.
//
// Why @dnd-kit: it's the React-native standard for accessible DnD. The
// SortableContext + useSortable hook handle keyboard reordering, focus
// management, and ARIA out of the box — important because this list
// has 100 items and we don't want to ship a custom keyboard story.
import { useMemo, useState } from "react";
import { Settings2, GripVertical, RotateCcw, Trophy, Zap } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AI_MASTERY_PATH,
  MASTERY_TIERS,
  type MasterySkill,
} from "@/data/aiMasteryPath";
import { useMasteryHeroPrefs } from "@/hooks/useMasteryHeroPrefs";

// One row in the drag list. Memoised by `num` so reorders don't trigger
// a full repaint of the 100 children.
function SortableSkillRow({
  skill,
  index,
  onLaunch,
}: {
  skill: MasterySkill;
  index: number;
  /** Click anywhere on the row body to launch into the chat shell. */
  onLaunch?: (skill: MasterySkill) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: skill.num });
  const meta = MASTERY_TIERS[skill.tier];
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 rounded-md border bg-card/80 px-2 py-1.5 text-left transition-colors ${
        isDragging ? "border-primary shadow-lg" : "border-border hover:border-primary/40"
      }`}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab active:cursor-grabbing touch-none p-1 -m-1 rounded text-muted-foreground hover:text-foreground"
        aria-label={`Reorder ${skill.label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {/* Whole-row launch button: tapping anywhere except the drag
          handle drops the visitor straight into the unified chat
          shell with the matching playbook prefilled. Keeps the
          settings popover from being a "look but don't touch"
          surface — these challenges are now actionable. */}
      <button
        type="button"
        onClick={() => onLaunch?.(skill)}
        className="flex-1 flex items-center gap-2 min-w-0 text-left"
        title={`Launch playbook for: ${skill.label}`}
      >
        <span className="text-[10px] font-mono text-muted-foreground w-6 shrink-0 tabular-nums">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-mono uppercase tracking-wider ${meta.pill}`}
        >
          <span className={`w-1 h-1 rounded-full ${meta.dot}`} aria-hidden />
          {meta.headline}
        </span>
        <span className="text-[11px] font-mono text-foreground truncate flex-1 group-hover:text-primary transition-colors">
          {skill.label}
        </span>
        <span className="text-[9px] font-mono text-muted-foreground shrink-0">
          #{skill.num}
        </span>
        <Zap
          className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          aria-hidden
        />
      </button>
    </li>
  );
}

interface Props {
  /** Click handler fired when the user taps any roadmap row in the
   *  popover. Parent uses this to launch the unified chat shell with
   *  the matching playbook so settings doubles as a quick-launch
   *  surface for the full 100. */
  onLaunchSkill?: (skill: MasterySkill) => void;
}

export default function MasteryHeroSettings({ onLaunchSkill }: Props = {}) {
  const { prefs, setRoadmapOrder, resetRoadmap } = useMasteryHeroPrefs();

  // Resolve the user's saved order into real skill objects. We fall
  // back to the canonical AI_MASTERY_PATH if no order is saved, and we
  // append any newly-added skills (future-proofing if the catalog
  // grows beyond 100) at the end so they never disappear.
  const orderedSkills = useMemo<MasterySkill[]>(() => {
    if (!prefs.roadmapOrder) return AI_MASTERY_PATH;
    const byNum = new Map(AI_MASTERY_PATH.map((s) => [s.num, s]));
    const seen = new Set<number>();
    const out: MasterySkill[] = [];
    for (const n of prefs.roadmapOrder) {
      const s = byNum.get(n);
      if (s && !seen.has(n)) {
        out.push(s);
        seen.add(n);
      }
    }
    for (const s of AI_MASTERY_PATH) {
      if (!seen.has(s.num)) out.push(s);
    }
    return out;
  }, [prefs.roadmapOrder]);

  // Local working copy so dragging feels instant; we persist on drop.
  const [workingOrder, setWorkingOrder] = useState<number[]>(() =>
    orderedSkills.map((s) => s.num),
  );

  // Re-sync the working list whenever the saved prefs change OR the
  // popover opens against a different baseline. Without this, a reset
  // wouldn't reflect inside the open popover.
  const [open, setOpen] = useState(false);
  const handleOpenChange = (next: boolean) => {
    if (next) setWorkingOrder(orderedSkills.map((s) => s.num));
    setOpen(next);
  };

  const sensors = useSensors(
    // Small activation distance so accidental clicks aren't treated as
    // drags — important on the launch buttons that share the popover.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWorkingOrder((items) => {
      const oldIndex = items.indexOf(active.id as number);
      const newIndex = items.indexOf(over.id as number);
      if (oldIndex < 0 || newIndex < 0) return items;
      const next = arrayMove(items, oldIndex, newIndex);
      // Persist on every drop so the change is sticky even if the user
      // closes the popover immediately after.
      setRoadmapOrder(next);
      return next;
    });
  };

  // Hydrate working list back to MasterySkill[] for rendering.
  const byNum = useMemo(
    () => new Map(AI_MASTERY_PATH.map((s) => [s.num, s])),
    [],
  );
  const renderSkills = workingOrder
    .map((n) => byNum.get(n))
    .filter((s): s is MasterySkill => Boolean(s));

  const isCustomOrder = prefs.roadmapOrder !== null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Customize learning roadmap"
          title="Customize your roadmap"
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg border border-border bg-card/60 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
        >
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(92vw,420px)] p-0 bg-card border-border"
      >
        {/* Drag-and-drop roadmap — the user's voyage. We render the
            full 100 inside a scroll area so they can plan a complete
            arc, not just shuffle the next three. */}
        <div className="p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Trophy className="w-3 h-3 text-primary shrink-0" />
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Your learning voyage
              </span>
              {isCustomOrder && (
                <span className="px-1 py-px rounded text-[9px] font-mono bg-primary/15 text-primary border border-primary/30">
                  Custom
                </span>
              )}
            </div>
            {isCustomOrder && (
              <button
                type="button"
                onClick={() => {
                  resetRoadmap();
                  setWorkingOrder(AI_MASTERY_PATH.map((s) => s.num));
                }}
                className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-foreground"
                title="Restore the curated order"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            )}
          </div>
          <p className="text-[10px] font-mono text-muted-foreground mb-2 leading-snug">
            Drag the handle to reorder, or tap any skill to launch a
            playbook for it right now.
          </p>

          <div className="max-h-[50vh] overflow-y-auto pr-1 -mr-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={workingOrder}
                strategy={verticalListSortingStrategy}
              >
                <ul className="flex flex-col gap-1">
                  {renderSkills.map((s, i) => (
                    <SortableSkillRow
                      key={s.num}
                      skill={s}
                      index={i}
                      onLaunch={(skill) => {
                        // Close the popover immediately so the chat
                        // shell takes over the viewport without the
                        // settings overlay covering it.
                        setOpen(false);
                        onLaunchSkill?.(skill);
                      }}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}